import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import html2canvas from "html2canvas";
import FileSaver from "file-saver";
import axios from "axios";
import { enUS } from "date-fns/locale";
import { DateRangePicker } from "react-date-range";
import { IoMdClose } from "react-icons/io";
import { format, isYesterday, isToday } from "date-fns";
import { useReactToPrint } from "react-to-print";
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  MdOutlineFileDownload,
  MdOutlineSort,
  MdDragHandle,
  MdOutlineTableChart,
  MdOutlineInsertChartOutlined,
} from "react-icons/md";
import { FaShareAlt } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiPlus, FiCheck, FiLayers } from "react-icons/fi";

import DonutChartGroups from "./Charts/DonutChartGroups";
import AudienceDataComponent from "./AddTable/Audiences";
import GenderInfo from "./AddTable/Genderinfo";
import DeviceTable from "./AddTable/DeviceTable";
import GenderChat from "./Charts/GenderChat";
import Agechart from "./Charts/Agechart";
import DeviceChart from "./Charts/DeviceChart";
import CustomTable from "./AddTable/CustomTable";
import Switcher7 from "./Tools/Switcher";
import { apiRequestBodyBuilder } from "./config/tableHelper";
import { handleCustomRepSharing } from "./AddTableCustomShare";

const REPORT_TYPE = {
  NONE: "none",
  GROUP: "group",
  AUDIENCE: "audience",
  GENDER: "gender",
  DEVICE: "device",
};

const ELEMENT_TYPE = {
  TABLE: "Table",
  CHART: "Chart",
};

const getElementKey = (reportType, elementType) => {
  if (!elementType || !reportType) return null;
  if (reportType === REPORT_TYPE.GROUP) {
    return elementType === ELEMENT_TYPE.CHART ? "group-chart" : "group-table";
  }
  return `${reportType}-${elementType.toLowerCase()}`;
};

const apiClient = axios.create({ baseURL: "https://api.confidanto.com" });

const getUserCredentials = () => ({
  customer_id: localStorage.getItem("customer_id"),
  project_id: localStorage.getItem("project_id"),
  email: localStorage.getItem("email"),
  ad_account_id: localStorage.getItem("ad_account_id"),
  platform: localStorage.getItem("platform"),
});

const buildApiPayload = (groupNames, dateRange) => {
  const { startDate, endDate } = dateRange;
  const payload = { ...getUserCredentials(), group_names: groupNames };
  if (startDate === endDate) {
    payload.single_date = startDate;
  } else {
    payload.start_date = startDate;
    payload.end_date = endDate;
  }
  return payload;
};

const addVisibleTable = (type, dateRange, config) => {
  return axios.post(
    "https://api.confidanto.com/custom-reporting-table-visibility",
    {
      project_id: localStorage.getItem("project_id"),
      type,
      dateRange,
      config,
    },
  );
};

const deleteVisibleTable = (type) => {
  return axios.post(
    "https://api.confidanto.com/custom-reporting-table-visibility",
    {
      project_id: localStorage.getItem("project_id"),
      type,
      delete: true,
    },
  );
};

const api = {
  fetchGroups: async () => {
    try {
      const credentials = getUserCredentials();
      const groupInfoResponse = await apiClient.post(
        "/campaign-group2/group-info",
        credentials,
      );
      const groupType = groupInfoResponse.data.type;
      const typeForQuery = groupType === "keyword" ? "keywords" : groupType;
      const finalRequestBody = { ...credentials, type: typeForQuery };
      const res = await apiClient.post(
        "/campaign-group/fetch-groups",
        finalRequestBody,
      );
      return res.data.groups;
    } catch (error) {
      console.error(
        "Failed to fetch groups:",
        error.response ? error.response.data : error.message,
      );
      return [];
    }
  },
  fetchGroupedMetricsTableData: async (groupNames, dateRange) => {
    const payload = buildApiPayload(groupNames, dateRange);
    const res = await apiClient.post("/campaign-group/datewise-data", payload);
    return res.data.metrics.map((group) => ({
      group_name: group.group_name,
      metrics: group.metrics[1].totals,
    }));
  },
  fetchGroupedMetricsChartData: async (groupNames, dateRange) => {
    const payload = buildApiPayload(groupNames, dateRange);
    const res = await apiClient.post(
      "/campaign-group/fetch-groups-metric-percentage",
      payload,
    );
    return res.data.filter((item) => groupNames.includes(item.group_name));
  },
};

const ChartWrapper = React.memo(
  ({ title, onRemove, children, isDownloading }) => (
    <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6 transition-all duration-200 hover:shadow-md print:shadow-none print:border-none print:break-inside-avoid print:p-2 print:mb-3">
      <div className="pdf-safe-table flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50 print:bg-white print:border-none print:px-0">
        <div className="flex items-center gap-2.5">
          <span className="w-2 h-2 rounded-full bg-blue-500 print:hidden" />
          <h2 className="text-base font-semibold text-slate-800 tracking-tight print:text-gray-900 print:font-bold">
            {title}
          </h2>
        </div>
        {!isDownloading && (
          <button
            title="Remove item"
            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors print:hidden"
            onClick={onRemove}
          >
            <IoMdClose className="text-lg" />
          </button>
        )}
      </div>
      <div className="p-6 overflow-x-auto print:p-0 print:overflow-visible">
        {children}
      </div>
    </div>
  ),
);

const ReportItem = React.memo(
  ({ selectionRange, element, onRemove, groups, isDownloading }) => {
    const [chartData, setChartData] = useState([]);
    const groupIds = useMemo(
      () => groups.map((item) => item.group_name),
      [groups],
    );
    const { type } = element;

    useEffect(() => {
      if (type === "group-chart" && groupIds.length > 0) {
        let requestBody = apiRequestBodyBuilder();
        requestBody.group_names = groupIds;
        axios
          .post(
            "https://api.confidanto.com/campaign-group/fetch-groups-metric-percentage",
            requestBody,
          )
          .then((res) => {
            const filteredData = res.data.filter((d) =>
              groupIds.includes(d.group_name),
            );
            setChartData(filteredData);
          })
          .catch((error) => {
            console.error("Error fetching group chart data:", error);
          });
      }
    }, [groupIds, type]);

    const previousDatesObj = useMemo(() => {
      if (!selectionRange || selectionRange.length === 0) return null;

      const dateRange = {
        startDate: format(selectionRange[0].startDate, "yyyy-MM-dd"),
        endDate: format(selectionRange[0].endDate, "yyyy-MM-dd"),
      };

      let obj = {
        curr_start_date: dateRange.startDate,
        curr_end_date: dateRange.endDate,
      };

      if (selectionRange.length > 1) {
        obj.prev_start_date = format(selectionRange[1].startDate, "yyyy-MM-dd");
        obj.prev_end_date = format(selectionRange[1].endDate, "yyyy-MM-dd");
      }
      return obj;
    }, [selectionRange]);

    const onRemoveClick = useCallback(() => onRemove(type), [onRemove, type]);

    if (!previousDatesObj) return null;

    const componentMap = {
      "group-table": (
        <ChartWrapper
          title="Group Data Table"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <CustomTable
            insightAvailable={true}
            initialDateRange={selectionRange}
          />
        </ChartWrapper>
      ),
      "group-chart": (
        <ChartWrapper
          title="Group Performance Chart"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <DonutChartGroups
            labels={groupIds}
            data={chartData}
            insightAvailable={true}
            previousDates={previousDatesObj}
          />
        </ChartWrapper>
      ),
      "audience-chart": (
        <ChartWrapper
          title="Audience Performance Chart"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <Agechart previousDates={previousDatesObj} />
        </ChartWrapper>
      ),
      "gender-chart": (
        <ChartWrapper
          title="Gender Performance Chart"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <GenderChat previousDates={previousDatesObj} />
        </ChartWrapper>
      ),
      "device-chart": (
        <ChartWrapper
          title="Device Performance Chart"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <DeviceChart previousDates={previousDatesObj} />
        </ChartWrapper>
      ),
      "audience-table": (
        <ChartWrapper
          title="Audience Data Table"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <AudienceDataComponent
            initialDateRange={selectionRange}
            insightAvailable={true}
          />
        </ChartWrapper>
      ),
      "gender-table": (
        <ChartWrapper
          title="Gender Data Table"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <GenderInfo
            insightAvailable={true}
            initialDateRange={selectionRange}
          />
        </ChartWrapper>
      ),
      "device-table": (
        <ChartWrapper
          title="Device Data Table"
          onRemove={onRemoveClick}
          isDownloading={isDownloading}
        >
          <DeviceTable
            initialDateRange={selectionRange}
            insightAvailable={true}
          />
        </ChartWrapper>
      ),
    };

    return componentMap[type] || null;
  },
);

const DraggableList = ({ items, setItems, onRemove }) => {
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", index);
    }
  };

  const handleDragEnter = (e, index) => {
    if (dragItem.current === index) return;
    dragOverItem.current = index;
    const newList = [...items];
    const dragItemContent = newList[dragItem.current];
    newList.splice(dragItem.current, 1);
    newList.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = dragOverItem.current;
    dragOverItem.current = null;
    setItems(newList);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDragEnd = () => {
    dragItem.current = null;
    dragOverItem.current = null;
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-xs text-slate-400 italic py-3 text-center bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
        No components added yet
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => (
        <div
          key={item.type}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragEnter={(e) => handleDragEnter(e, index)}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl shadow-xs cursor-grab active:cursor-grabbing hover:border-blue-400 hover:shadow-sm transition group"
        >
          <div className="flex items-center gap-3">
            <MdDragHandle className="text-slate-400 text-lg group-hover:text-blue-500 transition-colors" />
            <span className="capitalize text-sm text-slate-700 font-medium">
              {item.type.replace("-", " ")}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.type);
            }}
            className="text-slate-400 hover:text-rose-500 p-1 rounded-md hover:bg-rose-50 transition"
          >
            <IoMdClose />
          </button>
        </div>
      ))}
    </div>
  );
};

function CampaignPage() {
  const [reportElements, setReportElements] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const reportRef = useRef(null);

  const initialRange = useMemo(() => {
    const now = new Date();
    return [
      {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1),
        endDate: now,
        key: "selection",
      },
    ];
  }, []);

  const [selectionRange, setSelectionRange] = useState(initialRange);
  const [committedDateRange, setCommittedDateRange] = useState(initialRange);

  const [showForm, setShowForm] = useState(false);
  const [elementType, setElementType] = useState(null);
  const [reportType, setReportType] = useState(REPORT_TYPE.NONE);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const datePickerRef = useRef(null);
  const downloadRef = useRef(null);
  const chartDropdownRef = useRef(null);
  const tableDropdownRef = useRef(null);

  const [projectIconPrefixed, setProjectIconPrefixed] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  const handleApplyDate = () => {
    setCommittedDateRange(selectionRange);
    setShowDatePicker(false);
  };

  const handleOpenDatePicker = () => {
    setSelectionRange(committedDateRange);
    setShowDatePicker(true);
  };

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setElementType(null);
    setReportType(REPORT_TYPE.NONE);
    setSelectedGroups([]);
  }, []);

  const handleModeSelect = (selectedElementType) => {
    if (elementType === selectedElementType && showForm) {
      handleCloseForm();
      return;
    }
    setElementType(selectedElementType);
    setShowForm(true);
    setReportType(REPORT_TYPE.NONE);
    setSelectedGroups([]);
  };

  const previousDatesObj = useMemo(() => {
    if (!committedDateRange || committedDateRange.length === 0) return null;

    const dateRange = {
      startDate: format(committedDateRange[0].startDate, "yyyy-MM-dd"),
      endDate: format(committedDateRange[0].endDate, "yyyy-MM-dd"),
    };

    let obj = {
      curr_start_date: dateRange.startDate,
      curr_end_date: dateRange.endDate,
    };

    if (committedDateRange.length > 1) {
      obj.prev_start_date = format(
        committedDateRange[1].startDate,
        "yyyy-MM-dd",
      );
      obj.prev_end_date = format(committedDateRange[1].endDate, "yyyy-MM-dd");
    }
    return obj;
  }, [committedDateRange]);

  const fetchVisibleTables = useCallback(() => {
    setIsLoading(true);
    axios
      .post(
        "https://api.confidanto.com/custom-reporting-table-visibility/get",
        { project_id: localStorage.getItem("project_id") },
      )
      .then((res) => {
        setReportElements(res.data);
      })
      .catch((err) => {
        console.error("Error fetching visible tables:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const formatButtonLabel = () => {
    const { startDate, endDate } = committedDateRange[0];
    const formatDate = (date) =>
      isToday(date)
        ? `Today`
        : isYesterday(date)
          ? `Yesterday`
          : format(date, "MMM dd, yyyy");
    const startLabel = formatDate(startDate);
    const endLabel = formatDate(endDate);
    return startDate.getTime() === endDate.getTime()
      ? startLabel
      : `${startLabel} - ${endLabel}`;
  };

  const handlePrintPdf = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Campaign_Report_${format(new Date(), "yyyy-MM-dd")}`,
    pageStyle: `
      @media print {
        body { -webkit-print-color-adjust: exact; }
        @page { size: landscape; margin: 8mm; }
        html { zoom: 0.9; }
      }
    `,
    onBeforeGetContent: async () => {
      setIsDownloading(true);
      await new Promise((resolve) => setTimeout(resolve, 300));
    },
    onAfterPrint: () => setIsDownloading(false),
    onPrintError: () => setIsDownloading(false),
  });

  const handleDownloadDocs = async () => {
    setIsDownloading(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));

    if (!reportRef.current) {
      setIsDownloading(false);
      return;
    }
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 1400,
      });

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pageHeightInPixels = Math.floor(imgWidth * 1.41);

      let heightLeft = imgHeight;
      let position = 0;
      const imageSlices = [];

      while (heightLeft > 0) {
        const currentSliceHeight = Math.min(heightLeft, pageHeightInPixels);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = currentSliceHeight;
        const ctx = pageCanvas.getContext("2d");

        ctx.drawImage(
          canvas,
          0,
          position,
          imgWidth,
          currentSliceHeight,
          0,
          0,
          imgWidth,
          currentSliceHeight,
        );

        imageSlices.push(pageCanvas.toDataURL("image/png"));
        heightLeft -= pageHeightInPixels;
        position += pageHeightInPixels;
      }

      const htmlContent = `
          <!DOCTYPE html>
          <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
          <head>
            <meta charset="utf-8">
            <title>Report</title>
            <!--[if gte mso 9]>
            <xml>
            <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
            </xml>
            <![endif]-->
            <style>
              @page Section1 {
                size: 8.27in 11.69in;
                margin: 0.5in;
                mso-header-margin: 0.5in;
                mso-footer-margin: 0.5in;
                mso-paper-source: 0;
              }
              div.Section1 {
                page: Section1;
              }
            </style>
          </head>
          <body style="font-family: Arial, sans-serif; margin: 0; padding: 0;">
            <div class="Section1">
              ${imageSlices
                .map(
                  (imgData) =>
                    `<img src="${imgData}" width="650" style="width: 650px; max-width: 100%; display:block; margin: 0 auto;" />`,
                )
                .join("")}
            </div>
          </body>
          </html>
        `;

      const blob = new Blob(["\ufeff", htmlContent], {
        type: "application/msword",
      });
      FileSaver.saveAs(blob, "report.doc");
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const groups = await api.fetchGroups();
        setGroupData(groups);
      } catch (error) {
        console.error("Failed to load initial page data:", error);
      } finally {
        setIsLoading(false);
      }
      fetchVisibleTables();
    };
    loadInitialData();
  }, [fetchVisibleTables]);

  const handleDownload = async (formatSelection) => {
    if (isDownloading || isFetchingLogo) return;
    setShowDownloadOptions(false);
    setIsFetchingLogo(true);

    const proid = localStorage.getItem("project_id");
    const isProjectLogoVisible = localStorage.getItem(
      `showLogoProject_${proid}`,
    );

    if (isProjectLogoVisible === "true" || isProjectLogoVisible === true) {
      try {
        const payload = {
          customer_id: localStorage.getItem("customer_id"),
          project_id: localStorage.getItem("project_id"),
          email: localStorage.getItem("email"),
          start_date: format(committedDateRange[0].startDate, "yyyy-MM-dd"),
          end_date: format(committedDateRange[0].endDate, "yyyy-MM-dd"),
          table_names: ["audiences"],
          group_names: [],
          show_project_icon: isProjectLogoVisible,
          ad_account_id: localStorage.getItem("ad_account_id"),
          platform: localStorage.getItem("platform"),
        };
        const res = await axios.post(
          "https://api.confidanto.com/add-table-data",
          payload,
        );

        if (res.data && res.data.project_icon) {
          setProjectIconPrefixed(
            `data:image/jpeg;base64,${res.data.project_icon}`,
          );
        } else {
          setProjectIconPrefixed(null);
        }
      } catch (error) {
        console.error("Error fetching project icon:", error);
        setProjectIconPrefixed(null);
      }
    } else {
      setProjectIconPrefixed(null);
    }

    setIsFetchingLogo(false);

    if (formatSelection === "PDF") {
      handlePrintPdf();
    } else {
      handleDownloadDocs();
    }
  };

  const toggleVisibleTables = useCallback(
    async (element, isAdd) => {
      try {
        if (isAdd) {
          await addVisibleTable(
            element.type,
            element.dateRange,
            element.config,
          );
        } else {
          await deleteVisibleTable(element.type);
        }
      } catch (error) {
        console.error("Error saving or deleting table:", error);
        alert("An error occurred. Please try again.");
      } finally {
        fetchVisibleTables();
      }
    },
    [fetchVisibleTables],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadOptions(false);
      }

      if (showForm) {
        const activeContainer =
          elementType === ELEMENT_TYPE.TABLE
            ? tableDropdownRef.current
            : chartDropdownRef.current;

        if (
          activeContainer &&
          !activeContainer.contains(event.target) &&
          !event.target.closest?.(".MuiPopover-root") &&
          !event.target.closest?.(".MuiMenu-root")
        ) {
          handleCloseForm();
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm, elementType, handleCloseForm]);

  const existingElementTypes = useMemo(
    () => new Set(reportElements.map((item) => item.type)),
    [reportElements],
  );

  const isSelectedElement = (type, element) => {
    const key = getElementKey(type, element);
    return existingElementTypes.has(key);
  };

  const handleCreateClick = async () => {
    if (reportType === REPORT_TYPE.GROUP && selectedGroups.length === 0) {
      alert("Please select at least one group.");
      return;
    }

    const currentReportType = reportType;
    const currentElementType = elementType;
    const currentSelectedGroups = [...selectedGroups];

    handleCloseForm();

    const formattedDateRange = {
      startDate: format(selectionRange[0].startDate, "yyyy-MM-dd"),
      endDate: format(selectionRange[0].endDate, "yyyy-MM-dd"),
    };
    const newElementConfig = {
      type: getElementKey(currentReportType, currentElementType),
      config: {},
      dateRange: formattedDateRange,
    };

    if (currentReportType === REPORT_TYPE.GROUP) {
      newElementConfig.config.selectedGroupDetails = currentSelectedGroups.map(
        (id) => groupData.find((g) => g.group_id === id),
      );
    }
    toggleVisibleTables(newElementConfig, true);
  };

  const handleRemoveElement = useCallback(
    (typeToRemove) => {
      if (
        window.confirm("Are you sure you want to delete this report element?")
      ) {
        const elementToRemove = reportElements.find(
          (el) => el.type === typeToRemove,
        );
        if (elementToRemove) {
          toggleVisibleTables(elementToRemove, false);
        } else {
          console.error(
            "Could not find the element to remove with type:",
            typeToRemove,
          );
        }
      }
    },
    [reportElements, toggleVisibleTables],
  );

  function handleSelectDateRanges(ranges) {
    const key = Object.keys(ranges)[0];
    const values = Object.values(ranges)[0];
    const newSelectionRange = selectionRange.map((i) => {
      if (i.key === key) {
        return { ...i, startDate: values.startDate, endDate: values.endDate };
      }
      return i;
    });
    setSelectionRange(newSelectionRange);
  }

  function compareDateRanges() {
    const now = new Date();
    const currYear = now.getFullYear();
    const currMonth = now.getMonth();

    setSelectionRange((prev) =>
      prev.length > 1
        ? [prev[0]]
        : [
            ...prev,
            {
              startDate: new Date(currYear, currMonth - 1, 1),
              endDate: new Date(currYear, currMonth, 0),
              key: "compare",
            },
          ],
    );
  }

  const handleReorderTables = (newTables) => {
    setReportElements((prev) => {
      const others = prev.filter((el) => !el.type.endsWith("table"));
      return [...newTables, ...others];
    });
  };

  const handleReorderCharts = (newCharts) => {
    setReportElements((prev) => {
      const others = prev.filter((el) => !el.type.endsWith("chart"));
      return [...others, ...newCharts];
    });
  };

  const tableElements = useMemo(
    () => reportElements.filter((item) => item.type.endsWith("table")),
    [reportElements],
  );

  const chartElements = useMemo(
    () => reportElements.filter((item) => item.type.endsWith("chart")),
    [reportElements],
  );

  return (
    <Box className="min-h-screen bg-slate-50/75 text-slate-800 py-6">
      <div className="mx-5 space-y-6">
        {/* Top Control Bar */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 flex flex-col md:flex-row justify-between items-center gap-4 print:hidden">
          <div className="relative" ref={datePickerRef}>
            <button
              onClick={handleOpenDatePicker}
              className="w-56 px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg shadow-xs hover:border-blue-500 hover:bg-blue-50 transition"
            >
              {formatButtonLabel()}
            </button>
            {showDatePicker && (
              <div className="absolute z-30 mt-3 bg-white shadow-xl rounded-xl p-4 border border-gray-100 animate-fade-in">
                <DateRangePicker
                  onChange={handleSelectDateRanges}
                  showSelectionPreview={true}
                  direction="horizontal"
                  ranges={selectionRange}
                  maxDate={new Date()}
                  locale={enUS}
                />
                <div className="flex justify-between items-center mb-2 mx-2">
                  <button
                    onClick={handleApplyDate}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-center mt-2 transition text-sm font-medium shadow-xs"
                  >
                    Apply
                  </button>
                  <Switcher7
                    onToggle={compareDateRanges}
                    flag={selectionRange.length > 1}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Reorder Button */}
            <button
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-lg hover:bg-blue-50 hover:text-blue-600 transition"
              onClick={() => setShowReorderModal(true)}
            >
              <div className="flex flex-col justify-center items-center space-y-1">
                <MdOutlineSort className="text-lg" />
                <span className="text-xs font-medium">Reorder</span>
              </div>
            </button>

            {/* Share Button */}
            <button
              className={`flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-lg transition ${
                shareLoading || isDownloading || isFetchingLogo
                  ? "opacity-75 cursor-not-allowed text-gray-500"
                  : "hover:bg-blue-50 hover:text-blue-600"
              }`}
              onClick={async () => {
                if (shareLoading || isDownloading || isFetchingLogo) return;
                try {
                  const activeTableNames = [
                    ...new Set(
                      reportElements.map((el) => {
                        const base = el.type.split("-")[0];
                        return base === "audience" ? "audiences" : base;
                      }),
                    ),
                  ].filter(Boolean);

                  await handleCustomRepSharing({
                    previousDates: previousDatesObj,
                    tableNames:
                      activeTableNames.length > 0
                        ? activeTableNames
                        : ["audiences", "gender", "device"],
                    PageType: "custom_reporting",
                    setLoading: setShareLoading,
                  });
                } catch (error) {
                  console.error("Error in handleCustomRepSharing:", error);
                  alert("Failed to share report. Please try again.");
                }
              }}
              disabled={shareLoading || isDownloading || isFetchingLogo}
            >
              {shareLoading ? (
                <div className="flex flex-col justify-center items-center space-y-1">
                  <AiOutlineLoading3Quarters className="animate-spin text-lg" />
                  <span className="text-xs font-medium">Sharing...</span>
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center space-y-1">
                  <FaShareAlt className="text-lg" />
                  <span className="text-xs font-medium">Share</span>
                </div>
              )}
            </button>

            {/* Download Button */}
            <div className="relative" ref={downloadRef}>
              <button
                className="flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-lg hover:bg-blue-50 hover:text-blue-600 transition disabled:opacity-75 disabled:cursor-not-allowed"
                onClick={() =>
                  !isDownloading &&
                  !isFetchingLogo &&
                  !shareLoading &&
                  setShowDownloadOptions(!showDownloadOptions)
                }
                disabled={isDownloading || isFetchingLogo || shareLoading}
              >
                {isDownloading || isFetchingLogo ? (
                  <div className="flex items-center flex-col justify-center space-y-1">
                    <AiOutlineLoading3Quarters className="animate-spin text-lg" />
                    <span className="text-xs font-medium">Processing</span>
                  </div>
                ) : (
                  <div className="flex items-center flex-col justify-center space-y-1">
                    <MdOutlineFileDownload className="text-lg" />
                    <span className="text-xs font-medium">Download</span>
                  </div>
                )}
              </button>
              {showDownloadOptions &&
                !isDownloading &&
                !isFetchingLogo &&
                !shareLoading && (
                  <div className="absolute right-0 bg-white shadow-xl rounded-xl p-2 mt-2 z-20 border border-slate-200 w-40 animate-fade-in">
                    {["PDF", "Google Docs"].map((fmt) => (
                      <button
                        key={fmt}
                        onClick={() => handleDownload(fmt)}
                        className="block w-full text-left px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition"
                      >
                        {fmt}
                      </button>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Report Content Container */}
        <div
          className={`w-full flex flex-col items-center justify-center print:m-0 print:p-0 ${
            isDownloading ? "pt-0 bg-white" : ""
          }`}
          ref={reportRef}
        >
          {isDownloading && (
            <div className="w-full px-8 py-6 mb-4 flex justify-between items-center bg-white border-b border-gray-200">
              <div className="w-1/3 text-left">
                {projectIconPrefixed ? (
                  <img
                    src={projectIconPrefixed}
                    alt="Project Logo"
                    style={{
                      maxWidth: "200px",
                      objectFit: "contain",
                    }}
                  />
                ) : null}
              </div>
              <div className="w-1/3 text-center">
                <h1 className="text-2xl font-bold text-gray-800 uppercase whitespace-nowrap">
                  Performance Report
                </h1>
              </div>
              <div className="w-1/3 text-right">
                <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg text-sm border border-gray-200">
                  Date: {formatButtonLabel()}
                </span>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-500">
              <CircularProgress
                size={26}
                thickness={4}
                className="text-blue-600"
              />
              <span className="text-sm font-medium">
                Loading saved reports...
              </span>
            </div>
          ) : (
            <div className="w-full space-y-8">
              {/* Tables Section */}
              <section className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200 print:hidden">
                  <div className="flex items-center gap-2">
                    <MdOutlineTableChart className="text-blue-600 text-xl" />
                    <h3 className="text-base font-semibold text-slate-800">
                      Data Tables
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                      {tableElements.length}
                    </span>
                  </div>

                  <div className="flex items-center flex-wrap gap-2.5">
                    {!(showForm && elementType === ELEMENT_TYPE.TABLE) ? (
                      <button
                        onClick={() => handleModeSelect(ELEMENT_TYPE.TABLE)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 shadow-2xs transition"
                      >
                        <FiPlus className="text-sm" />
                        <span>Add Table</span>
                      </button>
                    ) : (
                      <div
                        ref={tableDropdownRef}
                        className="flex items-center flex-wrap gap-2 bg-white p-2 rounded-xl border border-slate-200 shadow-sm animate-fade-in"
                      >
                        <FormControl size="small" className="w-44">
                          <InputLabel className="text-xs">
                            Select Table Type
                          </InputLabel>
                          <Select
                            value={reportType}
                            label="Select Table Type"
                            onChange={(e) => setReportType(e.target.value)}
                            sx={{
                              fontSize: "0.85rem",
                              height: 36,
                              borderRadius: "0.5rem",
                            }}
                          >
                            <MenuItem
                              value={REPORT_TYPE.GROUP}
                              disabled={isSelectedElement(
                                REPORT_TYPE.GROUP,
                                elementType,
                              )}
                            >
                              Group
                            </MenuItem>
                            <MenuItem
                              value={REPORT_TYPE.AUDIENCE}
                              disabled={isSelectedElement(
                                REPORT_TYPE.AUDIENCE,
                                elementType,
                              )}
                            >
                              Audience
                            </MenuItem>
                            <MenuItem
                              value={REPORT_TYPE.GENDER}
                              disabled={isSelectedElement(
                                REPORT_TYPE.GENDER,
                                elementType,
                              )}
                            >
                              Gender
                            </MenuItem>
                            <MenuItem
                              value={REPORT_TYPE.DEVICE}
                              disabled={isSelectedElement(
                                REPORT_TYPE.DEVICE,
                                elementType,
                              )}
                            >
                              Device
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {reportType === REPORT_TYPE.GROUP && (
                          <FormControl size="small" className="w-48">
                            <InputLabel className="text-xs">
                              Select Groups
                            </InputLabel>
                            <Select
                              multiple
                              value={selectedGroups}
                              label="Select Groups"
                              onChange={(e) =>
                                setSelectedGroups(e.target.value)
                              }
                              renderValue={(selectedIds) =>
                                selectedIds
                                  .map(
                                    (id) =>
                                      groupData.find((g) => g.group_id === id)
                                        ?.group_name,
                                  )
                                  .join(", ")
                              }
                              sx={{
                                fontSize: "0.85rem",
                                height: 36,
                                borderRadius: "0.5rem",
                              }}
                            >
                              {groupData.map((group) => (
                                <MenuItem
                                  key={group.group_id}
                                  value={group.group_id}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={selectedGroups.includes(
                                      group.group_id,
                                    )}
                                  />
                                  <ListItemText
                                    primaryTypographyProps={{
                                      fontSize: "0.85rem",
                                    }}
                                    primary={group.group_name}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {reportType !== REPORT_TYPE.NONE && (
                          <button
                            onClick={handleCreateClick}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 h-9 rounded-lg transition shadow-xs flex items-center gap-1.5"
                          >
                            <FiCheck className="text-sm" />
                            Create
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={handleCloseForm}
                          title="Close"
                          className="w-9 h-9 flex items-center justify-center text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-lg transition shadow-xs"
                          aria-label="Close select"
                        >
                          <IoMdClose className="text-lg" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {tableElements.length === 0 ? (
                  <div className="w-full py-8 border-2 border-dashed border-slate-200/90 rounded-2xl flex flex-col items-center justify-center text-center bg-white/40 print:hidden">
                    <MdOutlineTableChart className="text-slate-300 text-3xl mb-2" />
                    <p className="text-xs font-semibold text-slate-600">
                      No tables added yet
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Click "+ Add Table" above to add group, audience, gender,
                      or device data.
                    </p>
                  </div>
                ) : (
                  <div className="w-full">
                    {tableElements.map((element) => (
                      <ReportItem
                        selectionRange={committedDateRange}
                        key={element.type}
                        element={element}
                        onRemove={handleRemoveElement}
                        groups={groupData}
                        isDownloading={isDownloading}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Charts Section */}
              <section className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200 print:hidden">
                  <div className="flex items-center gap-2">
                    <MdOutlineInsertChartOutlined className="text-indigo-600 text-xl" />
                    <h3 className="text-base font-semibold text-slate-800">
                      Visual Charts
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                      {chartElements.length}
                    </span>
                  </div>

                  <div className="flex items-center flex-wrap gap-2.5">
                    {!(showForm && elementType === ELEMENT_TYPE.CHART) ? (
                      <button
                        onClick={() => handleModeSelect(ELEMENT_TYPE.CHART)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 shadow-2xs transition"
                      >
                        <FiPlus className="text-sm" />
                        <span>Add Chart</span>
                      </button>
                    ) : (
                      <div
                        ref={chartDropdownRef}
                        className="flex items-center flex-wrap gap-2 bg-white p-2 rounded-xl border border-slate-200 shadow-sm animate-fade-in"
                      >
                        <FormControl size="small" className="w-44">
                          <InputLabel className="text-xs">
                            Select Chart Type
                          </InputLabel>
                          <Select
                            value={reportType}
                            label="Select Chart Type"
                            onChange={(e) => setReportType(e.target.value)}
                            sx={{
                              fontSize: "0.85rem",
                              height: 36,
                              borderRadius: "0.5rem",
                            }}
                          >
                            <MenuItem
                              value={REPORT_TYPE.GROUP}
                              disabled={isSelectedElement(
                                REPORT_TYPE.GROUP,
                                elementType,
                              )}
                            >
                              Group
                            </MenuItem>
                            <MenuItem
                              value={REPORT_TYPE.AUDIENCE}
                              disabled={isSelectedElement(
                                REPORT_TYPE.AUDIENCE,
                                elementType,
                              )}
                            >
                              Audience
                            </MenuItem>
                            <MenuItem
                              value={REPORT_TYPE.GENDER}
                              disabled={isSelectedElement(
                                REPORT_TYPE.GENDER,
                                elementType,
                              )}
                            >
                              Gender
                            </MenuItem>
                            <MenuItem
                              value={REPORT_TYPE.DEVICE}
                              disabled={isSelectedElement(
                                REPORT_TYPE.DEVICE,
                                elementType,
                              )}
                            >
                              Device
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {reportType === REPORT_TYPE.GROUP && (
                          <FormControl size="small" className="w-48">
                            <InputLabel className="text-xs">
                              Select Groups
                            </InputLabel>
                            <Select
                              multiple
                              value={selectedGroups}
                              label="Select Groups"
                              onChange={(e) =>
                                setSelectedGroups(e.target.value)
                              }
                              renderValue={(selectedIds) =>
                                selectedIds
                                  .map(
                                    (id) =>
                                      groupData.find((g) => g.group_id === id)
                                        ?.group_name,
                                  )
                                  .join(", ")
                              }
                              sx={{
                                fontSize: "0.85rem",
                                height: 36,
                                borderRadius: "0.5rem",
                              }}
                            >
                              {groupData.map((group) => (
                                <MenuItem
                                  key={group.group_id}
                                  value={group.group_id}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={selectedGroups.includes(
                                      group.group_id,
                                    )}
                                  />
                                  <ListItemText
                                    primaryTypographyProps={{
                                      fontSize: "0.85rem",
                                    }}
                                    primary={group.group_name}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {reportType !== REPORT_TYPE.NONE && (
                          <button
                            onClick={handleCreateClick}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-4 h-9 rounded-lg transition shadow-xs flex items-center gap-1.5"
                          >
                            <FiCheck className="text-sm" />
                            Create
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={handleCloseForm}
                          title="Close"
                          className="w-9 h-9 flex items-center justify-center text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-lg transition shadow-xs"
                          aria-label="Close select"
                        >
                          <IoMdClose className="text-lg" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {chartElements.length === 0 ? (
                  <div className="w-full py-8 border-2 border-dashed border-slate-200/90 rounded-2xl flex flex-col items-center justify-center text-center bg-white/40 print:hidden">
                    <MdOutlineInsertChartOutlined className="text-slate-300 text-3xl mb-2" />
                    <p className="text-xs font-semibold text-slate-600">
                      No charts added yet
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Click "+ Add Chart" above to view metrics in donut and bar
                      charts.
                    </p>
                  </div>
                ) : (
                  <div className="w-full">
                    {chartElements.map((element) => (
                      <ReportItem
                        selectionRange={committedDateRange}
                        key={element.type}
                        element={element}
                        onRemove={handleRemoveElement}
                        groups={groupData}
                        isDownloading={isDownloading}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>

      {/* Drag & Drop Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5 border border-slate-100 animate-fade-in">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FiLayers className="text-blue-600 text-lg" />
                <h3 className="text-base font-semibold text-slate-800">
                  Reorder Components
                </h3>
              </div>
              <button
                onClick={() => setShowReorderModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>

            <div className="flex flex-col gap-6 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Tables
                  </h4>
                  <span className="text-[11px] text-slate-400 font-medium">
                    {tableElements.length} items
                  </span>
                </div>
                <DraggableList
                  items={tableElements}
                  setItems={handleReorderTables}
                  onRemove={handleRemoveElement}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Charts
                  </h4>
                  <span className="text-[11px] text-slate-400 font-medium">
                    {chartElements.length} items
                  </span>
                </div>
                <DraggableList
                  items={chartElements}
                  setItems={handleReorderCharts}
                  onRemove={handleRemoveElement}
                />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setShowReorderModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-xl transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}

export default CampaignPage;
